/**
 * Agnes Image 2.1 Flash Adapter
 *
 * Uses OpenAI-compatible synchronous API format.
 * Endpoint: POST https://apihub.agnes-ai.com/v1/images/generations
 *
 * Model: agnes-image-2.1-flash
 * Features: text-to-image, image-to-image, high-density image optimization
 *
 * API docs: https://agnes-ai.com/doc/agnes-image-21-flash
 */

import type {
  ImageGenerationConfig,
  ImageGenerationOptions,
  ImageGenerationResult,
} from '../types';

const DEFAULT_MODEL = 'agnes-image-2.1-flash';
const DEFAULT_BASE_URL = 'https://apihub.agnes-ai.com/v1';

export async function testAgnesImageConnectivity(
  config: ImageGenerationConfig,
): Promise<{ success: boolean; message: string }> {
  const baseUrl = config.baseUrl || DEFAULT_BASE_URL;
  try {
    const response = await fetch(`${baseUrl}/images/generations`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${config.apiKey}`,
      },
      body: JSON.stringify({
        model: config.model || DEFAULT_MODEL,
        prompt: 'test',
        size: '1024x1024',
        response_format: 'url',
      }),
    });
    if (response.status === 401 || response.status === 403) {
      const text = await response.text();
      return { success: false, message: `Agnes auth failed (${response.status}): ${text}` };
    }
    return { success: true, message: 'Connected to Agnes Image' };
  } catch (err) {
    return { success: false, message: `Agnes connectivity error: ${err}` };
  }
}

export async function generateWithAgnesImage(
  config: ImageGenerationConfig,
  options: ImageGenerationOptions,
): Promise<ImageGenerationResult> {
  const baseUrl = config.baseUrl || DEFAULT_BASE_URL;

  const response = await fetch(`${baseUrl}/images/generations`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${config.apiKey}`,
    },
    body: JSON.stringify({
      model: config.model || DEFAULT_MODEL,
      prompt: options.prompt,
      size: options.aspectRatio || '1:1',
      response_format: 'url',
    }),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Agnes Image generation failed (${response.status}): ${text}`);
  }

  const data = await response.json();
  const imageData = data.data?.[0];
  if (!imageData) {
    throw new Error('Agnes Image returned empty response');
  }

  return {
    url: imageData.url,
    width: 1024,
    height: 1024,
  };
}
